/**
 * Converts English/Latin numbers (0-9) to Arabic-Eastern numerals (٠-٩)
 * @param {string|number} input - The number or string containing numbers to convert
 * @param {boolean} isArabic - Whether to apply the conversion (when in Arabic mode)
 * @returns {string} - The converted string with Arabic numerals
 */
export const toArabicNumerals = (input, isArabic = false) => {
  if (!isArabic) return String(input);

  const arabicNumbers = {
    '0': '٠',
    '1': '١',
    '2': '٢',
    '3': '٣',
    '4': '٤',
    '5': '٥',
    '6': '٦',
    '7': '٧',
    '8': '٨',
    '9': '٩'
  };

  return String(input).replace(/[0-9]/g, (digit) => arabicNumbers[digit]);
};

/**
 * Formats a number with Arabic numerals if in Arabic mode
 * Useful for prices, stats, etc.
 */
export const formatNumber = (number, isArabic = false) => {
  const formatted = Number(number).toLocaleString('en-US');
  return toArabicNumerals(formatted, isArabic);
};

/**
 * Formats currency with Arabic numerals if in Arabic mode
 */
export const formatCurrency = (amount, currency = 'BHD', isArabic = false) => {
  const formatted = `${Number(amount).toFixed(2)} ${currency}`;
  return toArabicNumerals(formatted, isArabic);
};

/**
 * Month names in English and Arabic
 */
const monthNames = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
};

/**
 * Formats a date with month name and year
 * @param {Date} date - The date to format
 * @param {boolean} isArabic - Whether to use Arabic month names
 * @returns {string} - Formatted date like "January 2026" or "يناير ٢٠٢٦"
 */
export const formatMonthYear = (date, isArabic = false) => {
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  const monthName = isArabic ? monthNames.ar[monthIndex] : monthNames.en[monthIndex];
  return `${monthName} ${toArabicNumerals(year, isArabic)}`;
};

/**
 * Formats a full date with month, day, and year
 * @param {Date} date - The date to format
 * @param {boolean} isArabic - Whether to use Arabic
 * @returns {string} - Formatted date like "January 15, 2026" or "١٥ يناير، ٢٠٢٦"
 */
export const formatFullDate = (date, isArabic = false) => {
  const monthIndex = date.getMonth();
  const day = date.getDate();
  const year = date.getFullYear();
  const monthName = isArabic ? monthNames.ar[monthIndex] : monthNames.en[monthIndex];

  if (isArabic) {
    return `${toArabicNumerals(day, true)} ${monthName}، ${toArabicNumerals(year, true)}`;
  }
  return `${monthName} ${day}, ${year}`;
};

/**
 * Subject name translations
 */
const subjectTranslations = {
  // Sciences
  'Chemistry': 'كيمياء',
  'Physics': 'فيزياء',
  'Biology': 'أحياء',
  'Science': 'علوم',

  // Mathematics
  'Mathematics': 'رياضيات',
  'Math': 'رياضيات',
  'Algebra': 'جبر',
  'Geometry': 'هندسة',
  'Calculus': 'حساب التفاضل والتكامل',
  'Statistics': 'إحصاء',

  // Languages
  'English': 'إنجليزي',
  'Arabic': 'عربي',
  'French': 'فرنسي',
  'Spanish': 'إسباني',
  'German': 'ألماني',

  // Humanities
  'History': 'تاريخ',
  'Geography': 'جغرافيا',
  'Social Studies': 'دراسات اجتماعية',
  'Islamic Studies': 'دراسات إسلامية',
  'Religion': 'دين',
  'Psychology': 'علم النفس',
  'Philosophy': 'فلسفة',
  'Literature': 'أدب',
  'Sociology': 'علم الاجتماع',
  'Political Science': 'علوم سياسية',
  'Anthropology': 'أنثروبولوجيا',

  // Other
  'Computer Science': 'علوم الحاسوب',
  'Programming': 'برمجة',
  'Economics': 'اقتصاد',
  'Business': 'أعمال',
  'Accounting': 'محاسبة',
  'Art': 'فن',
  'Music': 'موسيقى',
  'Physical Education': 'تربية بدنية'
};

/**
 * Translates a subject name to Arabic
 * @param {string} subject - The subject name in English
 * @param {boolean} isArabic - Whether to translate to Arabic
 * @returns {string} - The translated subject name or original if not found
 */
export const translateSubject = (subject, isArabic = false) => {
  if (!isArabic || !subject) return subject;
  return subjectTranslations[subject] || subject;
};

/**
 * Language name translations
 */
const languageTranslations = {
  'English': 'الإنجليزية',
  'Arabic': 'العربية',
  'French': 'الفرنسية',
  'Spanish': 'الإسبانية',
  'German': 'الألمانية',
  'Russian': 'الروسية',
  'Chinese': 'الصينية',
  'Japanese': 'اليابانية',
  'Korean': 'الكورية',
  'Hindi': 'الهندية',
  'Urdu': 'الأردية',
  'Bengali': 'البنغالية',
  'Italian': 'الإيطالية',
  'Portuguese': 'البرتغالية',
  'Turkish': 'التركية'
};

/**
 * Translates a language name to Arabic
 * @param {string} language - The language name in English
 * @param {boolean} isArabic - Whether to translate to Arabic
 * @returns {string} - The translated language name or original if not found
 */
export const translateLanguage = (language, isArabic = false) => {
  if (!isArabic || !language) return language;
  return languageTranslations[language] || language;
};

/**
 * Grade level translations
 */
const gradeTranslations = {
  'Elementary': 'الابتدائية',
  'Middle School': 'المتوسطة',
  'High School': 'الثانوية',
  'University': 'الجامعية',
  'Adult': 'الكبار',
  'Kindergarten': 'الروضة',
  'Preschool': 'ما قبل المدرسة',
  'Primary': 'الابتدائية',
  'Secondary': 'الثانوية',
  'Undergraduate': 'الجامعية',
  'Graduate': 'الدراسات العليا',
  // Specific grades
  'Grade 1': 'الصف الأول',
  'Grade 2': 'الصف الثاني',
  'Grade 3': 'الصف الثالث',
  'Grade 4': 'الصف الرابع',
  'Grade 5': 'الصف الخامس',
  'Grade 6': 'الصف السادس',
  'Grade 7': 'الصف السابع',
  'Grade 8': 'الصف الثامن',
  'Grade 9': 'الصف التاسع',
  'Grade 10': 'الصف العاشر',
  'Grade 11': 'الصف الحادي عشر',
  'Grade 12': 'الصف الثاني عشر',
  // University levels
  'Foundation': 'سنة تأسيسية',
  'Diploma': 'دبلوم',
  'Bachelor': 'بكالوريوس',
  'Master': 'ماجستير',
  'PhD': 'دكتوراه'
};

/**
 * Translates a grade level name to Arabic
 * @param {string} grade - The grade level name in English
 * @param {boolean} isArabic - Whether to translate to Arabic
 * @returns {string} - The translated grade level name or original if not found
 */
export const translateGrade = (grade, isArabic = false) => {
  if (!isArabic || !grade) return grade;
  return gradeTranslations[grade] || grade;
};

/**
 * Common Arabic name transliterations
 * Maps common Latin-script Arabic names to their Arabic script equivalents
 * Includes common Arabic names and special Bahraini names
 */
const nameTranslations = {
  // Common first names
  'Mohamed': 'محمد',
  'Muhammad': 'محمد',
  'Mohammed': 'محمد',
  'Ahmed': 'أحمد',
  'Ahmad': 'أحمد',
  'Ali': 'علي',
  'ALi': 'علي',
  'ALI': 'علي',
  'Hassan': 'حسن',
  'Hussein': 'حسين',
  'Husain': 'حسين',
  'Sayed': 'سيد',
  'Yousif': 'يوسف',
  'Yusuf': 'يوسف',
  'Ibrahim': 'إبراهيم',
  'Abdulla': 'عبد الله',
  'Abdullah': 'عبد الله',
  'Omar': 'عمر',
  'Umar': 'عمر',
  'Khaled': 'خالد',
  'Khalid': 'خالد',
  'Salman': 'سلمان',
  'Saeed': 'سعيد',
  'Said': 'سعيد',
  'Majed': 'ماجد',
  'Nasser': 'ناصر',
  'Fahad': 'فهد',
  'Tariq': 'طارق',
  'Hamad': 'حمد',
  'Hamdan': 'حمدان',
  'Hamed': 'حامد',
  'Hamid': 'حميد',
  
  // More common Arabic names
  'Mahmoud': 'محمود',
  'Mahmud': 'محمود',
  'Mahmood': 'محمود',
  'Othman': 'عثمان',
  'Osman': 'عثمان',
  'Rashid': 'راشد',
  'Rashed': 'راشد',
  'Khalifa': 'خليفة',
  'Khalifah': 'خليفة',
  'Mansoor': 'منصور',
  'Mansur': 'منصور',
  'Abdul': 'عبد',
  'Abdulrahman': 'عبد الرحمن',
  'Abdurrahman': 'عبد الرحمن',
  'Abdulaziz': 'عبد العزيز',
  'Yasser': 'ياسر',
  'Yaser': 'ياسر',
  'Ayman': 'أيمن',
  'Ammar': 'عمار',
  'Amin': 'أمين',
  'Anwar': 'أنور',
  'Adel': 'عادل',
  'Adil': 'عادل',
  'Akram': 'أكرم',
  'Bader': 'بدر',
  'Badr': 'بدر',
  'Bilal': 'بلال',
  'Bashar': 'بشار',
  'Faisal': 'فيصل',
  'Faycal': 'فيصل',
  'Farouk': 'فاروق',
  'Farouq': 'فاروق',
  'Ghassan': 'غسان',
  'Hani': 'هاني',
  'Hossam': 'حسام',
  'Hisham': 'هشام',
  'Jalal': 'جلال',
  'Jamil': 'جميل',
  'Karim': 'كريم',
  'Kamal': 'كمال',
  'Layth': 'ليث',
  'Malik': 'مالك',
  'Mazen': 'مازن',
  'Murad': 'مراد',
  'Nabil': 'نبيل',
  'Nader': 'نادر',
  'Naseem': 'نسيم',
  'Nasim': 'نسيم',
  'Qasim': 'قاسم',
  'Rami': 'رامي',
  'Rayan': 'ريان',
  'Saad': 'سعد',
  'Sadiq': 'صادق',
  'Samir': 'سمير',
  'Tamer': 'تامر',
  'Waleed': 'وليد',
  'Walid': 'وليد',
  'Younes': 'يونس',
  'Yunus': 'يونس',
  'Zaher': 'زاهر',
  'Zahir': 'زاهر',
  'Zaid': 'زيد',
  'Zayd': 'زيد',
  'Zaki': 'زكي',
  
  // Bahraini-specific names
  'Abdulhameed': 'عبد الحميد',
  'Abdulnabi': 'عبد النبي',
  'Abdulwahab': 'عبد الوهاب',
  'Adnan': 'عدنان',
  'Alawi': 'علوي',
  'Aqeel': 'عقيل',
  'Aqil': 'عقيل',
  'Baqer': 'باقر',
  'Baqir': 'باقر',
  'Dheya': 'ضياء',
  'Dhiya': 'ضياء',
  'Fadhel': 'فاضل',
  'Fadhil': 'فاضل',
  'Fakhir': 'فاخر',
  'Fakher': 'فاخر',
  'Ghazi': 'غازي',
  'Hasan': 'حسن',
  'Hashim': 'هاشم',
  'Haydar': 'حيدر',
  'Hidar': 'حيدر',
  'Jaafar': 'جعفر',
  'Jafar': 'جعفر',
  'Jawad': 'جواد',
  'Kadhim': 'كاظم',
  'Kadhem': 'كاظم',
  'Mahdi': 'مهدي',
  'Mahdy': 'مهدي',
  'Makki': 'مكي',
  'Mansour': 'منصور',
  'Mousa': 'موسى',
  'Musa': 'موسى',
  'Muhsin': 'محسن',
  'Muhsein': 'محسن',
  'Mustafa': 'مصطفى',
  'Mustapha': 'مصطفى',
  'Naji': 'نجي',
  'Naser': 'ناصر',
  'Qassem': 'قاسم',
  'Radhi': 'راضي',
  'Radi': 'راضي',
  'Rasheed': 'راشد',
  'Redha': 'رضا',
  'Ridha': 'رضا',
  'Rida': 'رضا',
  'Salih': 'صالح',
  'Saleh': 'صالح',
  'Salem': 'سالم',
  'Samad': 'صمد',
  'Shaikh': 'شيخ',
  'Sheikh': 'شيخ',
  'Taher': 'طاهر',
  'Tahir': 'طاهر',
  'Wahab': 'وهاب',
  'Wahhab': 'وهاب',
  'Yaseen': 'ياسين',
  'Yasin': 'ياسين',
  'Zain': 'زين',
  'Zayn': 'زين',
  'Zainal': 'زينال',
  'Zainul': 'زين العابدين',
  
  // Common last names/titles (including Bahraini)
  'Hilal': 'هلال',
  'Marhoon': 'مرهون',
  'Hussain': 'حسين',
  'Hassan': 'حسن',
  'Alkhalifa': 'الخليفة',
  'Al Khalifa': 'الخليفة',
  'Almarhoon': 'المرهون',
  'Al Marhoon': 'المرهون',
  'Alhilal': 'الهلال',
  'Al Hilal': 'الهلال',
  'Almansoor': 'المنصور',
  'Al Mansoor': 'المنصور',
  'Alhussain': 'الحسين',
  'Al Hussain': 'الحسين',
  'Alali': 'العلي',
  'Al Ali': 'العلي',
  'Alhassan': 'الحسن',
  'Al Hassan': 'الحسن',
  'Alsaad': 'السعد',
  'Al Saad': 'السعد',
  'Aljameel': 'الجميل',
  'Al Jameel': 'الجميل',
  'Alrasheed': 'الراشد',
  'Al Rasheed': 'الراشد',
  'Alhameed': 'الحميد',
  'Al Hameed': 'الحميد',
  'Alfadhel': 'الفاضل',
  'Al Fadhel': 'الفاضل',
  'Albaqer': 'الباقر',
  'Al Baqer': 'الباقر',
};

/**
 * Checks if a string contains Arabic characters
 */
const hasArabicCharacters = (str) => {
  if (!str) return false;
  // Arabic Unicode range: \u0600-\u06FF
  return /[\u0600-\u06FF]/.test(str);
};

/**
 * Transliterates a name using LibreTranslate API fallback if not in dictionary
 * @param {string} text - The text to transliterate
 * @param {string} sourceLang - Source language (default: 'en')
 * @param {string} targetLang - Target language (default: 'ar')
 * @returns {Promise<string>} - The transliterated text
 */
const transliterateViaAPI = async (text, sourceLang = 'en', targetLang = 'ar') => {
  try {
    // Using LibreTranslate API endpoint
    // You can replace this with your own LibreTranslate instance or other transliteration API
    // Set REACT_APP_TRANSLATE_API_URL environment variable to use a custom endpoint
    // Example: REACT_APP_TRANSLATE_API_URL=https://libretranslate.de/translate
    const apiUrl = process.env.REACT_APP_TRANSLATE_API_URL || 'https://libretranslate.de/translate';
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        source: sourceLang,
        target: targetLang,
        format: 'text'
      })
    });
    
    if (!response.ok) {
      throw new Error('Translation API request failed');
    }
    
    const data = await response.json();
    return data.translatedText || text;
  } catch (error) {
    console.warn('Transliteration API error:', error);
    // Return original text if API fails
    return text;
  }
};

// Cache for API-transliterated names to avoid repeated API calls
const nameTransliterationCache = new Map();

/**
 * Synchronous version - returns immediately if cached or dictionary match found
 * @param {string} name - The name in Latin script
 * @param {boolean} isArabic - Whether to translate to Arabic
 * @returns {string} - The transliterated name from cache/dictionary, or original if not found
 */
export const transliterateNameSync = (name, isArabic = false) => {
  if (!isArabic || !name) return name;
  
  // If name already contains Arabic characters, return as-is
  if (hasArabicCharacters(name)) return name;
  
  // Check cache first
  const cacheKey = `${name}_${isArabic}`;
  if (nameTransliterationCache.has(cacheKey)) {
    return nameTransliterationCache.get(cacheKey);
  }
  
  // Split name into parts
  const nameParts = name.split(' ').filter(part => part.trim());
  
  // Try to translate each part from dictionary
  const translatedParts = nameParts.map(part => {
    // Try multiple normalization formats to handle various cases like "ALi", "Ali", "ALI"
    const normalized1 = part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(); // "Ali"
    const normalized2 = part.charAt(0).toUpperCase() + part.slice(1); // "ALi"
    const normalized3 = part.toLowerCase(); // "ali"
    const normalized4 = part.toUpperCase(); // "ALI"
    
    if (nameTranslations[normalized1]) {
      return nameTranslations[normalized1];
    }
    if (nameTranslations[normalized2]) {
      return nameTranslations[normalized2];
    }
    if (nameTranslations[normalized3]) {
      return nameTranslations[normalized3];
    }
    if (nameTranslations[normalized4]) {
      return nameTranslations[normalized4];
    }
    if (nameTranslations[part]) {
      return nameTranslations[part];
    }
    return null;
  });
  
  // If all parts were translated from dictionary, cache and return
  const allTranslated = translatedParts.every(part => part !== null);
  if (allTranslated) {
    const result = translatedParts.join(' ');
    nameTransliterationCache.set(cacheKey, result);
    return result;
  }
  
  // Some parts not in dictionary - return original for now (API will handle it)
  return name;
};

/**
 * Transliterates a name to Arabic if possible
 * Uses dictionary first, then falls back to API transliteration if not found
 * @param {string} name - The name in Latin script
 * @param {boolean} isArabic - Whether to translate to Arabic
 * @param {boolean} useAPI - Whether to use API for names not in dictionary (default: true)
 * @returns {Promise<string>} - The transliterated name or original if not found
 */
export const transliterateName = async (name, isArabic = false, useAPI = true) => {
  if (!isArabic || !name) return name;
  
  // If name already contains Arabic characters, return as-is
  if (hasArabicCharacters(name)) return name;
  
  // Check cache first
  const cacheKey = `${name}_${isArabic}`;
  if (nameTransliterationCache.has(cacheKey)) {
    return nameTransliterationCache.get(cacheKey);
  }
  
  // Split name into parts
  const nameParts = name.split(' ').filter(part => part.trim());
  
  // Try to translate each part from dictionary
  const translatedParts = [];
  const untranslatedParts = [];
  
  for (let i = 0; i < nameParts.length; i++) {
    const part = nameParts[i];
    const normalized = part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    
    // Try dictionary lookup
    if (nameTranslations[normalized]) {
      translatedParts[i] = nameTranslations[normalized];
    } else if (nameTranslations[part]) {
      translatedParts[i] = nameTranslations[part];
    } else {
      // Not in dictionary - mark for API transliteration
      translatedParts[i] = null;
      untranslatedParts.push({ index: i, text: part });
    }
  }
  
  // If all parts were translated from dictionary, cache and return
  if (untranslatedParts.length === 0) {
    const result = translatedParts.join(' ');
    nameTransliterationCache.set(cacheKey, result);
    return result;
  }
  
  // If some parts are untranslated and API is enabled, use API for those parts
  if (useAPI && untranslatedParts.length > 0) {
    try {
      // Transliterate untranslated parts via API
      const apiPromises = untranslatedParts.map(async ({ index, text }) => {
        const transliterated = await transliterateViaAPI(text, 'en', 'ar');
        return { index, transliterated };
      });
      
      const apiResults = await Promise.all(apiPromises);
      
      // Fill in API transliterated parts
      apiResults.forEach(({ index, transliterated }) => {
        translatedParts[index] = transliterated;
      });
      
      // Return fully transliterated name and cache it
      const result = translatedParts.join(' ');
      nameTransliterationCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.warn('API transliteration failed, using dictionary only:', error);
      // Fall back to dictionary-only result (keep untranslated parts as-is)
      const result = translatedParts.map((part, idx) => part || nameParts[idx]).join(' ');
      nameTransliterationCache.set(cacheKey, result);
      return result;
    }
  }
  
  // If API not used or failed, return with untranslated parts as-is
  const result = translatedParts.map((part, idx) => part || nameParts[idx]).join(' ');
  nameTransliterationCache.set(cacheKey, result);
  return result;
};
